/**
 * service-mail bulk-mail client. Mirrors:
 *   apps/service-mail/src/modules/bulk-mail/bulk-mail.controller.ts
 *
 * RBAC: Mail/bulkSubmit for all calls.
 */
import { env } from '../env';

import { request } from './client';

export type BulkSubmissionStatus = 'UPLOADED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
export type BulkRowStatus = 'OK' | 'ERROR';

export interface BulkSubmissionDto {
  readonly id: string;
  readonly operatorId: string;
  readonly outletCode: string | null;
  readonly filename: string;
   
  readonly totalRows: number;
   
  readonly successRows: number;
   
  readonly errorRows: number;
  readonly status: BulkSubmissionStatus;
  readonly summary: unknown;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface BulkSubmissionRowDto {
  readonly id: string;
  readonly submissionId: string;
  readonly rowIndex: number;
  readonly status: BulkRowStatus;
  readonly errorMessage: string | null;
  readonly mailItemId: string | null;
  readonly inputPayload: unknown;
  readonly createdAt: string;
}

export interface BulkSubmissionDetailDto {
  readonly submission: BulkSubmissionDto;
  readonly rows: readonly BulkSubmissionRowDto[];
}

export interface ListBulkSubmissionsResponse {
  readonly items: readonly BulkSubmissionDto[];
  // eslint-disable-next-line upmp/no-number-money -- row count
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface BulkRowInput {
  readonly senderName: string;
  readonly senderPhone: string;
  readonly senderAddress: string;
  readonly receiverName: string;
  readonly receiverPhone: string;
  readonly receiverAddress: string;
   
  readonly weightGrams: number;
   
  readonly declaredValueMinor: number;
  readonly serviceClass: 'STANDARD' | 'EXPRESS' | 'EMS' | 'REGISTERED_MAIL';
}

export interface SubmitBulkBody {
  readonly filename: string;
  readonly outletCode?: string;
  readonly rows: readonly unknown[];
}

const baseUrl = (): string => env.serviceMailBaseUrl;

export async function submitBulkMail(body: SubmitBulkBody): Promise<BulkSubmissionDetailDto> {
  return request<BulkSubmissionDetailDto>(baseUrl(), '/v1/bulk-submissions', { method: 'POST', body });
}

export async function listBulkSubmissions(query: { page?: number; pageSize?: number } = {}): Promise<ListBulkSubmissionsResponse> {
  return request<ListBulkSubmissionsResponse>(baseUrl(), '/v1/bulk-submissions', {
    method: 'GET',
    query: {
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

export async function getBulkSubmission(id: string): Promise<BulkSubmissionDetailDto> {
  return request<BulkSubmissionDetailDto>(baseUrl(), `/v1/bulk-submissions/${id}`, { method: 'GET' });
}
